//April23 SubmissionTableToolbar:
import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box, Button, IconButton, Tooltip, Popover, TextField, Chip, Menu, MenuItem, List, ListItem, ListItemIcon, Switch
} from '@mui/material';
import {
  Event as EventIcon,
  ViewColumn as ViewColumnIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';

import ListItemText from '@mui/material/ListItemText';
import ColumnManagerDrawer from './ColumnManagerDrawer';
import { useFormSubmissionSettings } from '../../contexts/FormSubmissionSettingsContext';

import { safeSetItem, safeGetItem } from "../../utils/storageUtils";
import { useFormSubmissions } from '../../contexts/FormSubmissionsContext'; // Import context
import { useFormContext } from '../../contexts/FormContext';               

import ExportOptions from '../ExportOptions/ExportOptions';

/**
 * SubmissionTableToolbar
 *
 * Includes:
 * - Date range selector popover
 * - Multi-chip search bar
 * - Column visibility toggle
 * - Export button
 *
 * Pressing "Enter" in the input adds a new chip,
 * removing a chip updates the search,
 * and clicking the X button clears all terms.
 *
 * The search icon has been removed, but we preserve the same overall
 * padding and vertical size via minHeight in the search bar.
 */
const SubmissionTableToolbar = ({
  columns,
  data,
  onSearch,
  onDateRangeChange,
  columnOrder = [],
  setColumnOrder,
  resetToDefaults,
  hasVisibleProviderMetrics = false,
  providerMetricsRefreshing = false,
  onRefreshProviderMetrics = null,
}) => {
  const { updateTableSurfaceSettings } = useFormSubmissionSettings(); 

  const [searchTerm, setSearchTerm] = useState('');      // Single input value
  const [searchTerms, setSearchTerms] = useState([]);    // All “chip” queries

  // For date range popover
  const [dateRangeAnchorEl, setDateRangeAnchorEl] = useState(null);

/* ------------------------------------------------------------------ */
/* Predefined date ranges                                             */
/* ------------------------------------------------------------------ */
/*
   Helper utilities keep the logic readable.  They do NOT change any
   behaviour elsewhere in the file.
*/
const startOfDay = (d) => {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);           // 00:00:00.000
  return copy;
};

const endOfDay = (d) => {
  const copy = new Date(d);
  copy.setHours(23, 59, 59, 999);      // 23:59:59.999
  return copy;
};

  /* PRE-SELECT “Last 7 Days” (stable) ------------------------------------- */
  const last7 = useMemo(() => {
    const today  = new Date();              // now
    const sixAgo = new Date(today);
    sixAgo.setDate(today.getDate() - 6);    // 6 days back
    sixAgo.setHours(0, 0, 0, 0);            // 00:00:00.000
    return [sixAgo, today];
  }, []);


 const [customRange, setCustomRange] = useState([new Date(), new Date()]);
  const [selectedDateRange, setSelectedDateRange] = useState('Last 7 Days'); // ✅ Default range label

  // For column visibility popover
  const [menuAnchor, setMenuAnchor] = useState(null);
 // const [isModalOpen, setIsModalOpen] = useState(false);
 const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Ref for the search input
  const searchInputRef = useRef(null);

// Prevent double-firing the initial date range (mount + page fetch overlap)
const firedInitialRangeRef = useRef(false);

  const { submissions } = useFormSubmissions(); // Correct data source
  const { formId }      = useFormContext();     // ← build per-form storage key

  /** ───────────────────────────────────────────────────────────
   * Persisting column prefs
   *   • one key per form (`submission_<formId>_tableSettings`)
   *   • load settings **once** after both `columns` & `storageKey`
   *     are known – then never again (avoids infinite loop)
   * ─────────────────────────────────────────────────────────── */
  const storageKey = formId
    ? `submission_${formId}_tableSettings`
    : `submission_tableSettings`;  // fallback for safety

  const hasLoadedSettingsRef = useRef(false);   // belt-&-suspenders guard

useEffect(() => {
  if (!columns || columns.length === 0) return;
  if (hasLoadedSettingsRef.current) return;

  console.log('[SubmissionTableToolbar] Loading column prefs from', storageKey);

  const saved = safeGetItem(storageKey);

  if (saved?.columnOrder?.length) {
    console.log('[SubmissionTableToolbar] Applying saved columnOrder → shared settings (submissionsPageTable)');
    // ⬇️ push into the shared bucket so SubmissionTable.columns rebuilds & adopts
    updateTableSurfaceSettings('submissionsPageTable', { columnOrder: saved.columnOrder });
  } else {
    console.log('[SubmissionTableToolbar] No saved settings – using defaults in shared bucket');
    updateTableSurfaceSettings('submissionsPageTable', {
      columnOrder: columns.map((col, idx) => ({
        ...col,
        visible: true,
        order: idx,
      })),
    });
  }

  hasLoadedSettingsRef.current = true;
}, [columns, storageKey, updateTableSurfaceSettings]);


/**
 *  dateRanges:
 *  ───────────
 *  • 'Today'      → 00:00 today → now
 *  • 'Yesterday'  → full previous day (00:00 → 23:59)
 *  • 'Last 7 Days'→ midnight 6 days ago → now   (i.e. today counts as day 7)
 *
 *  NOTE: `endOfDay()` isn’t strictly required for the upper-bound because
 *  the Firestore query already bumps the end date to 23:59:59.999, but it
 *  makes the range clearer and consistent for all presets.
 */
const dateRanges = {
  // TODAY: midnight -> this very moment
  'Today': [
    startOfDay(new Date()),   // 00:00:00.000 of today
    new Date()                // now (inclusive upper-bound handled in query)
  ],

  // YESTERDAY: full previous calendar day
  'Yesterday': (() => {
    const y = new Date();
    y.setDate(y.getDate() - 1);
    return [startOfDay(y), endOfDay(y)];
  })(),

  // LAST 7 DAYS: midnight 6 days ago -> now
  'Last 7 Days': [
    startOfDay(new Date(new Date().setDate(new Date().getDate() - 6))),
    new Date()
  ],
};

   /* ────────────────────────────────────────────────────────────────────────
     FIRE THE INITIAL 7-DAY WINDOW ONLY IF WE DON'T ALREADY HAVE ROWS.
     This avoids a "second first page" fetch that causes duplicate rows.
  ──────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (firedInitialRangeRef.current) return;
    if (!Array.isArray(submissions)) return;

    if (submissions.length === 0) {
      console.log('[SubmissionTableToolbar] sending initial “Last 7 Days” range (list empty)');
      onDateRangeChange(last7);
    } else {
      console.log('[SubmissionTableToolbar] initial range suppressed (list already populated)');
    }
    firedInitialRangeRef.current = true;
  }, [submissions, onDateRangeChange, last7]);
 

  /**
   * Display either a predefined range label
   * or the actual date strings if "Custom" is selected.
   */
  const displayDateRange = () => {
    if (selectedDateRange === 'Custom') {
      return `${customRange[0].toLocaleDateString()} - ${customRange[1].toLocaleDateString()}`;
    }
    return selectedDateRange;
  };

  /** Opens the date range popover menu */
  const handleDateRangeMenu = (event) => {
    setDateRangeAnchorEl(event.currentTarget);
  };

  /** Closes the date range popover menu */
  const handleDateRangeClose = () => {
    setDateRangeAnchorEl(null);
  };

  /**
   * Select one of the predefined date ranges (Today, Yesterday, Last 7 Days).
   * This also calls onDateRangeChange(newRange) so the parent can filter by those dates.
   */
  const selectPredefinedRange = (rangeKey) => {
    const newRange = dateRanges[rangeKey];
    setSelectedDateRange(rangeKey);
    setCustomRange(newRange);
    onDateRangeChange(newRange);
    handleDateRangeClose();
  };

  /**
   * Called whenever user changes either start or end date in the custom range
   * from the DesktopDatePickers. Also sets selectedDateRange to 'Custom'.
   */
  const handleCustomDateChange = (date, index) => {
    const newRange = [...customRange];
    newRange[index] = date;
    setCustomRange(newRange);
    setSelectedDateRange('Custom');
    onDateRangeChange(newRange);
  };


  // ====================== COLUMN MANAGEMENT LOGIC ============================
  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleOpenModal = () => {
   setIsDrawerOpen(true);
    handleMenuClose(); // Close menu when opening modal
  };

  const handleCloseModal = () => {
   setIsDrawerOpen(false);
  };

const handleConfirmColumnChanges = (updatedColumnOrder) => {
  console.log('[SubmissionTableToolbar] Confirming column changes:', updatedColumnOrder);
  setColumnOrder(updatedColumnOrder);

  console.log('[SubmissionTableToolbar] Persisting new columnOrder to', storageKey);
  safeSetItem(storageKey, { columnOrder: updatedColumnOrder });

  // keep the shared source-of-truth aligned
  try {
    updateTableSurfaceSettings('submissionsPageTable', { columnOrder: updatedColumnOrder });
  } catch (e) {
    console.warn('[SubmissionTableToolbar] Failed to persist shared columnOrder:', e?.message || e);
  }
};


  /**
   * Anytime `searchTerms` changes (adding or removing chips), we call onSearch().
   * Currently, no additional filters are passed, but you could extend to include date ranges, etc.
   */
  useEffect(() => {
    console.log('[SubmissionTableToolbar] searchTerms changed:', searchTerms);
    onSearch(searchTerms, {});
  }, [searchTerms, onSearch]);

  /** Clears all current search terms and resets the input */
  const handleClearAllSearch = () => {
    console.log('[SubmissionTableToolbar] Clearing all search terms...');
    setSearchTerm('');
    setSearchTerms([]);
  };

  /**
   * Refresh provider-owned table metrics for the currently visible table view.
   *
   * Enterprise integration note:
   * - This is toolbar-level, not column-header-level, so it scales when more
   *   provider metric columns are added later.
   * - The toolbar does not know Bitly or any provider API details.
   * - The parent table owns the provider metric sync orchestration.
   */
  const handleRefreshProviderMetrics = () => {
    console.log('[SubmissionTableToolbar] Refresh provider metrics clicked:', {
      hasVisibleProviderMetrics,
      providerMetricsRefreshing,
    });

    if (typeof onRefreshProviderMetrics !== 'function') {
      console.warn(
        '[SubmissionTableToolbar] Provider metric refresh handler is not available.'
      );
      return;
    }

    onRefreshProviderMetrics();
  };

// 🔄 Align `columns` with visibility settings from `columnOrder`
//const mergedColumns = columns.map((col) => {
//  const matchingColumn = columnOrder.find((order) => order.id === col.id);
//  return {
//    ...col,
 //   isVisible: matchingColumn?.visible ?? true, // Default to visible if missing
 // };
//});

const mergedColumns = useMemo(() => {
  return columns.map((col) => {
    const match = columnOrder.find((o) => o.id === col.id);
    return {
      ...col,
      isVisible: match?.visible ?? true,  // default → visible
    };
  });
}, [columns, columnOrder]);

const exportVisibleColumns = useMemo(
  () => mergedColumns.filter((c) => c.isVisible),
  [mergedColumns],
);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', padding: '16px', gap: 2 }}>
      {/* ==================== DATE RANGE PICKER ===================== */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Button startIcon={<EventIcon />} onClick={handleDateRangeMenu}>
          {displayDateRange()} {/* ✅ Displays 'Today' or selected range */}
        </Button>
        <Popover
          open={Boolean(dateRangeAnchorEl)}
          anchorEl={dateRangeAnchorEl}
          onClose={handleDateRangeClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Box p={2}>
            {Object.keys(dateRanges).map((rangeKey) => (
              <Button key={rangeKey} onClick={() => selectPredefinedRange(rangeKey)}>
                {rangeKey}
              </Button>
            ))}
            <Box my={2}>
              <DesktopDatePicker
                label="Start Date"
                inputFormat="MM/dd/yyyy"
                value={customRange[0]}
                onChange={(date) => handleCustomDateChange(date, 0)}
                renderInput={(params) => <TextField {...params} />}
              />
              <DesktopDatePicker
                label="End Date"
                inputFormat="MM/dd/yyyy"
                value={customRange[1]}
                onChange={(date) => handleCustomDateChange(date, 1)}
                renderInput={(params) => <TextField {...params} />}
              />
            </Box>
          </Box>
        </Popover>
      </LocalizationProvider>

{/* ==================== SEARCH BAR WITH CHIPS ===================== */}
<Box
  sx={{
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '2px 8px',
    minHeight: '40px', // Ensure consistent height
  }}
>
  {/* Render a Chip for each search term */}
  {searchTerms.map((term, index) => (
    <Chip
      key={index}
      label={term}
      onDelete={() => {
        console.log(`[SubmissionTableToolbar] Removing chip: "${term}"`);
        // Remove the chip, triggers useEffect -> onSearch
        setSearchTerms((prev) => prev.filter((_, i) => i !== index));
      }}
      color="primary"
      size="small"
      sx={{ marginRight: '8px' }}
    />
  ))}

  <input
    ref={searchInputRef}
    type="text"
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' && searchTerm.trim() !== '') {
        console.log(`[SubmissionTableToolbar] Adding new search term: "${searchTerm.trim()}"`);
        // Prevent duplicate terms
        if (!searchTerms.includes(searchTerm.trim())) {
          setSearchTerms((prev) => [...prev, searchTerm.trim()]);
        }
        setSearchTerm('');
      } 
      else if (e.key === 'Backspace' && searchTerm.trim() === '') {
        // ✅ Handle backspace deletion of the last chip
        if (searchTerms.length > 0) {
          const lastTerm = searchTerms[searchTerms.length - 1];
          console.log(`[SubmissionTableToolbar] Backspace detected. Removing last chip: "${lastTerm}"`);
          setSearchTerms((prev) => prev.slice(0, -1));
        }
      }
    }}
    style={{
      border: 'none',
      outline: 'none',
      flexGrow: 1,
      paddingLeft: 0,
      lineHeight: '1.5',
    }}
    placeholder="Search..."
  />

  {/* Clear All Icon */}
  {searchTerms.length > 0 && (
    <Tooltip title="Clear All Searches">
      <IconButton
        size="small"
        onClick={handleClearAllSearch}
        sx={{ marginLeft: '4px' }}
      >
        <CloseIcon />
      </IconButton>
    </Tooltip>
  )}
</Box>

{/* ==================== COLUMN MANAGEMENT MODAL TRIGGER ===================== */}
<Tooltip title="Manage Columns">
  <IconButton size="small" onClick={handleOpenModal}>
    <ViewColumnIcon />
  </IconButton>
</Tooltip>

<ColumnManagerDrawer
  open={isDrawerOpen}
  onClose={handleCloseModal}
  scope="submissionsPage"     // writes to 'submissionsPageTable'
  persistPerUser={false}      // set true if you want roaming prefs per user
/>


{/* ==================== PROVIDER METRICS REFRESH ===================== */}
{hasVisibleProviderMetrics && (
  <Tooltip
    title={
      providerMetricsRefreshing
        ? 'Refreshing provider metrics...'
        : 'Refresh provider metrics'
    }
  >
    <span>
      <IconButton
        size="small"
        onClick={handleRefreshProviderMetrics}
        disabled={
          providerMetricsRefreshing ||
          typeof onRefreshProviderMetrics !== 'function'
        }
        aria-label="Refresh provider metrics"
      >
        <RefreshIcon
          fontSize="small"
          sx={{
            animation: providerMetricsRefreshing
              ? 'providerMetricSpin 1s linear infinite'
              : 'none',
            '@keyframes providerMetricSpin': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
          }}
        />
      </IconButton>
    </span>
  </Tooltip>
)}


      {/* ==================== EXPORT ICON / OPTIONS ===================== */}
<ExportOptions
  data={data || []}  
//  columns={mergedColumns.filter(col => col.isVisible)}  // ✅ Now using merged columns
  columns={exportVisibleColumns}       // ✅ memo-ised, no re-alloc every render
  columnOrder={columnOrder}  // ✅ Pass `columnOrder` to maintain correct sequence
/>

    </Box>
  );
};

export default SubmissionTableToolbar;
